import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";
import useAuth from "../hooks/useAuth";

// ✅ Toast global
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip as MuiTooltip,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { DataGrid } from "@mui/x-data-grid";

// ===== Icons =====
import EditIcon from "@mui/icons-material/Edit";
import MapIcon from "@mui/icons-material/Map";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import SaveIcon from "@mui/icons-material/Save";

// ===== Export =====
import * as XLSX from "xlsx";

// ===== Mapa =====
import { MapContainer, TileLayer, Marker, LayersControl, Tooltip as LeafletTooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// =========================
// Leaflet icon fix (bundlers)
// =========================
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Centro (rojo)
const edificioIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Cierre (verde)
const cierreIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

export const IntervencionesGerente = () => {
  const toast = useToast();
  const { auth } = useAuth();

  // =========================
  // Estado: datos base
  // =========================
  const [ticajes, setTicajes] = useState([]); // ya enriquecidos y filtrados por equipo
  const [usuariosEquipo, setUsuariosEquipo] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [centros, setCentros] = useState([]);
  const [ordenes, setOrdenes] = useState([]);

  const [loading, setLoading] = useState(true);

  // =========================
  // Estado: filtros (MUI)
  // =========================
  const [filtroUsuario, setFiltroUsuario] = useState(null); // option | null
  const [filtroCliente, setFiltroCliente] = useState(null);
  const [filtroCentro, setFiltroCentro] = useState(null);
  const [filtroOrden, setFiltroOrden] = useState(null);

  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");

  // =========================
  // Estado: mapa (Dialog)
  // =========================
  const [mapOpen, setMapOpen] = useState(false);
  const [mapFocus, setMapFocus] = useState("inicio"); // "inicio" | "cierre"
  const [ubicacionInicio, setUbicacionInicio] = useState(null); // {lat,lng} | null
  const [ubicacionCierre, setUbicacionCierre] = useState(null);
  const [ubicacionCentro, setUbicacionCentro] = useState(null);

  // =========================
  // Estado: edición (Dialog)
  // =========================
  const [editOpen, setEditOpen] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [ticajeEditar, setTicajeEditar] = useState(null);
  const [editForm, setEditForm] = useState({
    inicio: "",
    fin: "",
    motivo_fin: "",
    observaciones_admin: "",
    comentarios: "",
    orden_id: "",
  });
  const [editError, setEditError] = useState("");

  // =========================
  // Helpers: fechas / duración
  // =========================
  const parseDateSafe = (value) => {
    if (!value) return null;
    const normalized =
      typeof value === "string" && value.includes(" ") && !value.includes("T")
        ? value.replace(" ", "T")
        : value;
    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const fmtDateTime = (value) => {
    if (!value) return "-";
    const d = parseDateSafe(value);
    if (!d) return "-";
    return d.toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const toDatetimeLocalString = (dateValue) => {
    if (!dateValue) return "";
    const d = parseDateSafe(dateValue);
    if (!d) return "";
    const pad = (n) => String(n).padStart(2, "0");
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const min = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  };

  const calcularDuracionMs = (ms) => {
    if (!ms || Number.isNaN(ms)) return "No disponible";
    const horas = Math.floor(ms / 3600000);
    const minutos = Math.floor((ms % 3600000) / 60000);
    return `${horas}h ${minutos}min`;
  };

  // =========================
  // Carga de datos (catálogos + ticajes) y enriquecimiento
  // =========================
  const cargarTodo = useCallback(async () => {
    if (!auth?.id) return;

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [resT, resU, resC, resCentros, resOts] = await Promise.all([
        fetch(`${Global.url}ticajes/finalizados`, { headers }),
        fetch(`${Global.url}usuarios`, { headers }),
        fetch(`${Global.url}clientes`, { headers }),
        fetch(`${Global.url}centros/todos`, { headers }),
        fetch(`${Global.url}ordenes`, { headers }),
      ]);

      const datosT = resT.ok ? await resT.json() : [];
      const datosU = resU.ok ? await resU.json() : [];
      const datosC = resC.ok ? await resC.json() : [];
      const datosCentros = resCentros.ok ? await resCentros.json() : [];
      const datosOts = resOts.ok ? await resOts.json() : [];

      if (!resT.ok) toast.error("No se pudieron cargar las intervenciones finalizadas.");
      if (!resU.ok) toast.warning("No se pudieron cargar usuarios (se mostrarán IDs).");
      if (!resC.ok) toast.warning("No se pudieron cargar clientes.");
      if (!resCentros.ok) toast.warning("No se pudieron cargar centros.");
      if (!resOts.ok) toast.warning("No se pudieron cargar OT.");

      // Equipo directo del gerente
      const equipo = (Array.isArray(datosU) ? datosU : []).filter(
        (u) => String(u.gerente_id) === String(auth?.id)
      );

      setUsuariosEquipo(Array.isArray(equipo) ? equipo : []);
      setClientes(Array.isArray(datosC) ? datosC : []);
      setCentros(Array.isArray(datosCentros) ? datosCentros : []);
      setOrdenes(Array.isArray(datosOts) ? datosOts : []);

      // Maps para enriquecer
      const usuariosById = new Map(
        (Array.isArray(datosU) ? datosU : []).map((u) => [String(u.id), u])
      );
      const clientesById = new Map(
        (Array.isArray(datosC) ? datosC : []).map((c) => [String(c.id), c])
      );
      const centrosById = new Map(
        (Array.isArray(datosCentros) ? datosCentros : []).map((c) => [String(c.id), c])
      );
      const ordenesById = new Map(
        (Array.isArray(datosOts) ? datosOts : []).map((o) => [String(o.id), o])
      );

      const nombreCompleto = (u) => [u?.nombre, u?.apellidos].filter(Boolean).join(" ").trim();

      const nombreUsuario = (id) => {
        if (!id) return "-";
        const u = usuariosById.get(String(id));
        return u ? (nombreCompleto(u) || u.nombre || `Usuario ${id}`) : `Usuario ${id}`;
      };

      const nombreCliente = (id) => {
        if (!id) return "-";
        const c = clientesById.get(String(id));
        return c ? (c.nombre || `Cliente ${id}`) : `Cliente ${id}`;
      };

      const nombreCentro = (id) => {
        if (!id) return "-";
        const c = centrosById.get(String(id));
        return c ? (c.nombre || `Centro ${id}`) : `Centro ${id}`;
      };

      const nombreOrden = (id) => {
        if (!id) return "-";
        const o = ordenesById.get(String(id));
        return o ? (o.titulo || `OT ${id}`) : `OT ${id}`;
      };

      const idsEquipo = new Set((Array.isArray(equipo) ? equipo : []).map((u) => String(u.id)));

      const enriquecidos = (Array.isArray(datosT) ? datosT : [])
        .filter((t) => idsEquipo.has(String(t.usuario_id)))
        .map((t) => {
          const iniMs = parseDateSafe(t.inicio)?.getTime() ?? 0;
          const finMs = parseDateSafe(t.fin)?.getTime() ?? 0;

          const motivo = (t?.motivo_fin || "").toLowerCase();
          const estado =
            motivo.includes("cancel") ? "Cancelado" : motivo.includes("manual") ? "Finalizado Manual" : "Finalizado";

          return {
            ...t,
            estado,
            duracionMs: finMs && iniMs ? finMs - iniMs : null,

            usuario: nombreUsuario(t.usuario_id),
            cliente: t.cliente || nombreCliente(t.cliente_id),
            centro: t.centro || nombreCentro(t.centro_id),
            orden_nombre: t.orden_nombre || nombreOrden(t.orden_id),

            modificado_por_nombre: t.creado_por_admin_id
              ? nombreUsuario(t.creado_por_admin_id)
              : t.creado_por_nombre || "-",
          };
        });

      setTicajes(enriquecidos);
    } catch (err) {
      console.error("Error al cargar datos:", err);
      toast.error("No se pudo conectar con el servidor.");
      setTicajes([]);
    } finally {
      setLoading(false);
    }
  }, [auth?.id, toast]);

  useEffect(() => {
    cargarTodo();
  }, [cargarTodo]);

  // =========================
  // Opciones para Autocomplete
  // =========================
  const usuarioOptions = useMemo(() => {
    return (usuariosEquipo || [])
      .slice()
      .sort((a, b) => `${a.nombre ?? ""} ${a.apellidos ?? ""}`.localeCompare(`${b.nombre ?? ""} ${b.apellidos ?? ""}`))
      .map((u) => ({
        id: String(u.id),
        label: [u.nombre, u.apellidos].filter(Boolean).join(" ").trim() || `Usuario ${u.id}`,
      }));
  }, [usuariosEquipo]);

  const clienteOptions = useMemo(() => {
    return (clientes || [])
      .slice()
      .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""))
      .map((c) => ({ id: String(c.id), label: c.nombre || `Cliente ${c.id}` }));
  }, [clientes]);

  const centroOptions = useMemo(() => {
    return (centros || [])
      .slice()
      .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""))
      .map((c) => ({ id: String(c.id), label: c.nombre || `Centro ${c.id}` }));
  }, [centros]);

  const ordenOptions = useMemo(() => {
    return (ordenes || [])
      .slice()
      .sort((a, b) => (a.titulo || "").localeCompare(b.titulo || ""))
      .map((o) => ({ id: String(o.id), label: o.titulo || `OT ${o.id}` }));
  }, [ordenes]);

  // =========================
  // Filtrado (derivado)
  // =========================
  const rows = useMemo(() => {
    const uId = filtroUsuario?.id ? String(filtroUsuario.id) : "";
    const cId = filtroCliente?.id ? String(filtroCliente.id) : "";
    const ceId = filtroCentro?.id ? String(filtroCentro.id) : "";
    const oId = filtroOrden?.id ? String(filtroOrden.id) : "";

    return (ticajes || [])
      .filter((t) => {
        const inicioISO = (parseDateSafe(t.inicio) || new Date(0)).toISOString().split("T")[0];

        const matchUsuario = !uId || String(t.usuario_id ?? "") === uId;
        const matchCliente = !cId || String(t.cliente_id ?? "") === cId;
        const matchCentro = !ceId || String(t.centro_id ?? "") === ceId;
        const matchOrden = !oId || String(t.orden_id ?? "") === oId;

        const matchFecha =
          !fechaDesde && !fechaHasta
            ? true
            : fechaDesde && !fechaHasta
            ? inicioISO === fechaDesde
            : fechaDesde && fechaHasta
            ? inicioISO >= fechaDesde && inicioISO <= fechaHasta
            : true;

        return matchUsuario && matchCliente && matchCentro && matchOrden && matchFecha;
      })
      .map((t) => ({
        ...t,
        inicio_txt: fmtDateTime(t.inicio),
        fin_txt: fmtDateTime(t.fin),
        duracion_txt: calcularDuracionMs(t.duracionMs),
        comentarios_txt: t.comentarios?.trim() ? t.comentarios : "Sin comentarios",
        observaciones_txt: t.observaciones_admin || t.comentario_admin || "-",
      }));
  }, [ticajes, filtroUsuario, filtroCliente, filtroCentro, filtroOrden, fechaDesde, fechaHasta]);

  // =========================
  // Acciones: limpiar filtros
  // =========================
  const limpiarFiltros = () => {
    setFiltroUsuario(null);
    setFiltroCliente(null);
    setFiltroCentro(null);
    setFiltroOrden(null);
    setFechaDesde("");
    setFechaHasta("");
  };

  // =========================
  // Exportaciones
  // =========================
  const exportarCSVReducido = (datos, nombreArchivo = "ticajes_equipo.csv") => {
    if (!Array.isArray(datos) || datos.length === 0) {
      toast.warning("No hay datos para exportar.");
      return;
    }

    const encabezados = [
      "Usuario",
      "OT",
      "Cliente",
      "Centro",
      "Inicio",
      "Fin",
      "Duración",
      "Estado",
      "En Rango (Inicio)",
      "Distancia (Inicio m)",
      "En Rango (Cierre)",
      "Distancia (Cierre m)",
      "Comentarios",
      "Modificado por",
      "Observaciones",
    ];

    const csvRows = [
      encabezados.join(","),
      ...datos.map((t) => {
        const fila = [
          t.usuario ?? "",
          t.orden_nombre ?? "",
          t.cliente ?? "",
          t.centro ?? "",
          t.inicio ? new Date(t.inicio).toLocaleString() : "-",
          t.fin ? new Date(t.fin).toLocaleString() : "-",
          calcularDuracionMs(t.duracionMs),
          t.estado ?? "-",
          t.en_rango ? "En rango" : "Fuera de rango",
          t.distancia_m != null ? t.distancia_m : "",
          t.en_rango_cierre == null ? "" : t.en_rango_cierre ? "En rango" : "Fuera de rango",
          t.distancia_m_cierre != null ? t.distancia_m_cierre : "",
          t.comentarios ?? "Sin comentarios",
          t.modificado_por_nombre || "-",
          t.observaciones_admin || t.comentario_admin || "-",
        ];
        return fila.map((val) => `"${String(val ?? "").replace(/"/g, '""')}"`).join(",");
      }),
    ];

    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + csvRows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", nombreArchivo);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportarExcel = (datos, nombreArchivo = "ticajes_equipo.xlsx") => {
    if (!Array.isArray(datos) || datos.length === 0) {
      toast.warning("No hay datos para exportar.");
      return;
    }

    const datosFormatoExcel = datos.map((t) => ({
      Usuario: t.usuario ?? "",
      OT: t.orden_nombre ?? "",
      Cliente: t.cliente ?? "",
      Centro: t.centro ?? "",
      Inicio: t.inicio ? new Date(t.inicio).toLocaleString() : "-",
      Fin: t.fin ? new Date(t.fin).toLocaleString() : "-",
      Duración: calcularDuracionMs(t.duracionMs),
      Estado: t.estado ?? "-",
      "En Rango (Inicio)": t.en_rango ? "En rango" : "Fuera de rango",
      "Distancia (Inicio m)": t.distancia_m != null ? t.distancia_m : "",
      "En Rango (Cierre)":
        t.en_rango_cierre == null ? "" : t.en_rango_cierre ? "En rango" : "Fuera de rango",
      "Distancia (Cierre m)": t.distancia_m_cierre != null ? t.distancia_m_cierre : "",
      Comentarios: t.comentarios ?? "Sin comentarios",
      "Modificado por": t.modificado_por_nombre || "-",
      Observaciones: t.observaciones_admin || t.comentario_admin || "-",
    }));

    const hoja = XLSX.utils.json_to_sheet(datosFormatoExcel);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Ticajes");
    XLSX.writeFile(libro, nombreArchivo);
  };

  // =========================
  // Mapa: abrir
  // =========================
  const abrirMapa = async (t, focus = "inicio") => {
    setMapFocus(focus);

    const ini = t.latitud && t.longitud ? { lat: Number(t.latitud), lng: Number(t.longitud) } : null;
    const fin =
      t.latitud_cierre && t.longitud_cierre
        ? { lat: Number(t.latitud_cierre), lng: Number(t.longitud_cierre) }
        : null;

    setUbicacionInicio(ini);
    setUbicacionCierre(fin);

    try {
      const res = await fetch(`${Global.url}centros/${t.centro_id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.latitud && data?.longitud) {
        setUbicacionCentro({ lat: parseFloat(data.latitud), lng: parseFloat(data.longitud) });
      } else {
        setUbicacionCentro(null);
      }
    } catch (err) {
      console.error("Error al obtener centro:", err);
      setUbicacionCentro(null);
    }

    setMapOpen(true);
  };

  // =========================
  // Edición: abrir/cerrar
  // =========================
  const abrirModalEditar = (t) => {
    setTicajeEditar(t);
    setEditForm({
      inicio: t.inicio ? toDatetimeLocalString(t.inicio) : "",
      fin: t.fin ? toDatetimeLocalString(t.fin) : "",
      motivo_fin: t.motivo_fin || "",
      observaciones_admin: t.observaciones_admin || t.comentario_admin || "",
      comentarios: t.comentarios || "",
      orden_id: t.orden_id ? String(t.orden_id) : "",
    });
    setEditError("");
    setEditOpen(true);
  };

  const cerrarModalEditar = () => {
    setEditOpen(false);
    setTicajeEditar(null);
    setEditForm({
      inicio: "",
      fin: "",
      motivo_fin: "",
      observaciones_admin: "",
      comentarios: "",
      orden_id: "",
    });
    setEditError("");
  };

  // =========================
  // Edición: submit
  // =========================
  const handleSubmitEditar = async () => {
    if (!ticajeEditar?.id) return;

    setEditError("");

    if (!editForm.inicio || !editForm.fin) {
      setEditError("Inicio y fin son obligatorios.");
      return;
    }

    const dInicio = new Date(editForm.inicio);
    const dFin = new Date(editForm.fin);
    if (Number.isNaN(dInicio.getTime()) || Number.isNaN(dFin.getTime()) || dFin < dInicio) {
      setEditError("La fecha de fin debe ser posterior al inicio.");
      return;
    }

    setSavingEdit(true);
    try {
      const res = await fetch(`${Global.url}ticajes/modificar/${ticajeEditar.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          inicio: editForm.inicio,
          fin: editForm.fin,
          motivo_fin: editForm.motivo_fin,
          observaciones_admin: editForm.observaciones_admin,
          comentarios: editForm.comentarios,
          orden_id: editForm.orden_id ? Number(editForm.orden_id) : null,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setEditError(data.message || "Error al modificar la intervención.");
        return;
      }

      toast.success("Intervención modificada correctamente.");
      cerrarModalEditar();
      await cargarTodo();
    } catch (err) {
      console.error(err);
      setEditError("Error de conexión con el servidor.");
    } finally {
      setSavingEdit(false);
    }
  };

  // =========================
  // Columnas DataGrid
  // =========================
  const columns = useMemo(
    () => [
      { field: "usuario", headerName: "Usuario", flex: 1, minWidth: 220 },
      { field: "orden_nombre", headerName: "OT", flex: 1, minWidth: 220 },
      { field: "cliente", headerName: "Cliente", flex: 1, minWidth: 220 },
      { field: "centro", headerName: "Centro", flex: 1, minWidth: 220 },
      { field: "inicio_txt", headerName: "Inicio", minWidth: 180 },
      { field: "fin_txt", headerName: "Fin", minWidth: 180 },
      { field: "duracion_txt", headerName: "Duración", minWidth: 140 },
      {
        field: "estado",
        headerName: "Estado",
        minWidth: 170,
        renderCell: (params) => {
          const v = params.value || "-";
          const color =
            v.toLowerCase().includes("cancel") ? "error" : v.toLowerCase().includes("manual") ? "warning" : "success";
          return <Chip size="small" label={v} color={color} variant="filled" />;
        },
      },
      {
        field: "rango",
        headerName: "Rango / Distancia",
        flex: 1,
        minWidth: 340,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const t = params.row;

          const inicioTxt = t.en_rango ? "En rango" : "Fuera de rango";
          const cierreTxt =
            t.en_rango_cierre == null ? "-" : t.en_rango_cierre ? "En rango" : "Fuera de rango";

          const inicioDist = t.distancia_m != null ? `${t.distancia_m} m` : "-";
          const cierreDist = t.distancia_m_cierre != null ? `${t.distancia_m_cierre} m` : "-";

          const canInicioMapa = t.latitud && t.longitud && t.distancia_m != null;
          const canCierreMapa = t.latitud_cierre && t.longitud_cierre && t.distancia_m_cierre != null;

          return (
            <Stack spacing={0.5} sx={{ py: 0.5, width: "100%" }}>
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", alignItems: "center" }}>
                <Chip
                  size="small"
                  label={`Inicio: ${inicioTxt}`}
                  color={t.en_rango ? "success" : "error"}
                  variant="filled"
                />
                <Chip
                  size="small"
                  label={`Cierre: ${cierreTxt}`}
                  color={t.en_rango_cierre == null ? "default" : t.en_rango_cierre ? "success" : "error"}
                  variant="filled"
                />
              </Stack>

              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", alignItems: "center" }}>
                {canInicioMapa ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<MapIcon />}
                    onClick={() => abrirMapa(t, "inicio")}
                  >
                    Inicio: {inicioDist}
                  </Button>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Inicio: -
                  </Typography>
                )}

                {canCierreMapa ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<MapIcon />}
                    onClick={() => abrirMapa(t, "cierre")}
                  >
                    Cierre: {cierreDist}
                  </Button>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Cierre: -
                  </Typography>
                )}
              </Stack>
            </Stack>
          );
        },
      },
      { field: "comentarios_txt", headerName: "Comentarios", flex: 1, minWidth: 260 },
      { field: "modificado_por_nombre", headerName: "Modificado por", flex: 1, minWidth: 220 },
      { field: "observaciones_txt", headerName: "Observaciones", flex: 1, minWidth: 260 },
      {
        field: "acciones",
        headerName: "Acciones",
        width: 110,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <MuiTooltip title="Editar intervención">
            <IconButton onClick={() => abrirModalEditar(params.row)} size="small">
              <EditIcon />
            </IconButton>
          </MuiTooltip>
        ),
      },
    ],
    []
  );

  // =========================
  // Render
  // =========================
  return (
    <Box className="pageContainer">
      <Box className="contentContainer">
        <Stack spacing={2}>
          {/* =========================
              Header
             ========================= */}
          <Box className="pageHeader">
            <Typography className="pageHeader__title" component="h1">
              Ticajes de tu equipo
            </Typography>
            <Typography className="pageHeader__subtitle">
              Intervenciones finalizadas del equipo asignado a tu gerencia.
            </Typography>
          </Box>

          {/* =========================
              Filtros + Export
             ========================= */}
          <Paper sx={{ p: 2 }}>
            <Box
              sx={{
                display: "grid",
                gap: 2,
                alignItems: "end",
                gridTemplateColumns: { xs: "1fr", md: "repeat(12, 1fr)" },
              }}
            >
              {/* Fila 1 */}
              <Autocomplete
                options={usuarioOptions}
                value={filtroUsuario}
                onChange={(_, v) => setFiltroUsuario(v)}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => <TextField {...params} label="Usuario" />}
                sx={{ gridColumn: { xs: "1 / -1", md: "span 3" } }}
              />

              <Autocomplete
                options={clienteOptions}
                value={filtroCliente}
                onChange={(_, v) => setFiltroCliente(v)}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => <TextField {...params} label="Cliente" />}
                sx={{ gridColumn: { xs: "1 / -1", md: "span 3" } }}
              />

              <Autocomplete
                options={centroOptions}
                value={filtroCentro}
                onChange={(_, v) => setFiltroCentro(v)}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => <TextField {...params} label="Centro" />}
                sx={{ gridColumn: { xs: "1 / -1", md: "span 3" } }}
              />

              <Autocomplete
                options={ordenOptions}
                value={filtroOrden}
                onChange={(_, v) => setFiltroOrden(v)}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => <TextField {...params} label="OT" />}
                sx={{ gridColumn: { xs: "1 / -1", md: "span 3" } }}
              />

              {/* Fila 2 */}
              <TextField
                label="Desde"
                type="date"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ gridColumn: { xs: "1 / -1", md: "span 3" } }}
              />
              <TextField
                label="Hasta"
                type="date"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ gridColumn: { xs: "1 / -1", md: "span 3" } }}
              />

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{ gridColumn: { xs: "1 / -1", md: "span 6" }, justifyContent: "flex-end" }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<ClearAllIcon />}
                  onClick={limpiarFiltros}
                >
                  Limpiar filtros
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => exportarCSVReducido(rows, "ticajes_equipo_filtrados.csv")}
                >
                  CSV filtrados
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => exportarExcel(rows, "ticajes_equipo_filtrados.xlsx")}
                >
                  Excel filtrados
                </Button>
              </Stack>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{ gridColumn: "1 / -1", justifyContent: "flex-end" }}
              >
                <Button
                  variant="text"
                  startIcon={<DownloadIcon />}
                  onClick={() => exportarCSVReducido(ticajes, "ticajes_equipo_todos.csv")}
                >
                  CSV todos
                </Button>
                <Button
                  variant="text"
                  startIcon={<DownloadIcon />}
                  onClick={() => exportarExcel(ticajes, "ticajes_equipo_todos.xlsx")}
                >
                  Excel todos
                </Button>
              </Stack>
            </Box>
          </Paper>

          {/* =========================
              Tabla
             ========================= */}
          <Paper sx={{ height: 680, p: 1 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row.id}
              disableRowSelectionOnClick
              loading={loading}
              pagination
              initialState={{
                pagination: { paginationModel: { page: 0, pageSize: 25 } },
                sorting: { sortModel: [{ field: "inicio_txt", sort: "desc" }] },
              }}
              pageSizeOptions={[10, 25, 50, 100]}
              getRowHeight={() => "auto"}
              sx={{
                "& .MuiDataGrid-columnHeaders": {
                  borderBottom: "1px solid rgba(0,0,0,0.08)",
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid rgba(0,0,0,0.06)",
                  alignItems: "stretch",
                  py: 1,
                },
              }}
              slots={{
                noRowsOverlay: () => (
                  <Box sx={{ p: 2, color: "text.secondary" }}>
                    {loading ? "Cargando…" : "No hay resultados con los filtros actuales."}
                  </Box>
                ),
              }}
            />
          </Paper>
        </Stack>
      </Box>

      {/* =========================
          Dialog: Edición
         ========================= */}
      <Dialog open={editOpen} onClose={cerrarModalEditar} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          Editar intervención
          <IconButton onClick={cerrarModalEditar} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Inicio"
              type="datetime-local"
              value={editForm.inicio}
              onChange={(e) => setEditForm((p) => ({ ...p, inicio: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              label="Fin"
              type="datetime-local"
              value={editForm.fin}
              onChange={(e) => setEditForm((p) => ({ ...p, fin: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              required
            />

            <TextField
              label="Motivo fin"
              value={editForm.motivo_fin}
              onChange={(e) => setEditForm((p) => ({ ...p, motivo_fin: e.target.value }))}
              placeholder="Motivo fin"
            />

            <Autocomplete
              options={ordenOptions}
              value={ordenOptions.find((o) => String(o.id) === String(editForm.orden_id)) || null}
              onChange={(_, v) => setEditForm((p) => ({ ...p, orden_id: v?.id || "" }))}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => <TextField {...params} label="OT" placeholder="Selecciona OT" />}
            />

            <TextField
              label="Observaciones"
              value={editForm.observaciones_admin}
              onChange={(e) => setEditForm((p) => ({ ...p, observaciones_admin: e.target.value }))}
              multiline
              minRows={2}
            />

            <TextField
              label="Comentarios"
              value={editForm.comentarios}
              onChange={(e) => setEditForm((p) => ({ ...p, comentarios: e.target.value }))}
              multiline
              minRows={2}
            />

            {editError && (
              <Typography variant="body2" color="error">
                {editError}
              </Typography>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={cerrarModalEditar}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmitEditar}
            startIcon={<SaveIcon />}
            disabled={savingEdit}
          >
            Guardar cambios
          </Button>
        </DialogActions>
      </Dialog>

      {/* =========================
          Dialog: Mapa
         ========================= */}
      <Dialog open={mapOpen} onClose={() => setMapOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          Ubicación del ticaje ({mapFocus === "cierre" ? "cierre" : "inicio"})
          <IconButton onClick={() => setMapOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {(ubicacionInicio || ubicacionCierre) ? (
            <Box sx={{ height: 460, width: "100%" }}>
              <MapContainer
                center={
                  mapFocus === "cierre"
                    ? ubicacionCierre
                      ? [ubicacionCierre.lat, ubicacionCierre.lng]
                      : ubicacionInicio
                      ? [ubicacionInicio.lat, ubicacionInicio.lng]
                      : [0, 0]
                    : ubicacionInicio
                    ? [ubicacionInicio.lat, ubicacionInicio.lng]
                    : ubicacionCierre
                    ? [ubicacionCierre.lat, ubicacionCierre.lng]
                    : [0, 0]
                }
                zoom={15}
                style={{ height: "460px", width: "100%" }}
              >
                <LayersControl position="topright">
                  <LayersControl.BaseLayer checked name="Satélite + Callejero">
                    <TileLayer
                      url={`https://api.maptiler.com/maps/hybrid/256/{z}/{x}/{y}.jpg?key=${process.env.REACT_APP_MAPTILER_KEY}`}
                      attribution='&copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a>'
                    />
                  </LayersControl.BaseLayer>

                  <LayersControl.BaseLayer name="Callejero">
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution="&copy; OpenStreetMap contributors"
                    />
                  </LayersControl.BaseLayer>
                </LayersControl>

                {ubicacionInicio && (
                  <Marker position={[ubicacionInicio.lat, ubicacionInicio.lng]}>
                    <LeafletTooltip direction="top" offset={[0, -10]}>
                      📍 Ticaje (inicio)
                    </LeafletTooltip>
                  </Marker>
                )}

                {ubicacionCierre && (
                  <Marker position={[ubicacionCierre.lat, ubicacionCierre.lng]} icon={cierreIcon}>
                    <LeafletTooltip direction="top" offset={[0, -10]}>
                      ✅ Ticaje (cierre)
                    </LeafletTooltip>
                  </Marker>
                )}

                {ubicacionCentro && (
                  <Marker position={[ubicacionCentro.lat, ubicacionCentro.lng]} icon={edificioIcon}>
                    <LeafletTooltip direction="top" offset={[0, -10]}>
                      🏢 Centro de trabajo
                    </LeafletTooltip>
                  </Marker>
                )}
              </MapContainer>
            </Box>
          ) : (
            <Box sx={{ p: 2, color: "text.secondary" }}>No hay coordenadas disponibles para este ticaje.</Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={() => setMapOpen(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
